import React, { useEffect, useMemo, useState } from 'react';
import authService, { HomepageCategory } from '../services/auth.service';
import '../components/ModuleAccessGrid.css';

interface ModuleOption {
  id: string;
  name: string;
}

interface HomepageSettingsProps {
  modules: ModuleOption[];
  onSaved?: (categories: HomepageCategory[]) => void;
}

const newCategoryId = () => `cat_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const HomepageSettings: React.FC<HomepageSettingsProps> = ({ modules, onSaved }) => {
  const [categories, setCategories] = useState<HomepageCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const assignableModules = useMemo(
    () =>
      modules
        .filter((module) => !['dashboard', 'rustdesk', 'portal'].includes(module.id.toLowerCase()))
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' })),
    [modules]
  );

  useEffect(() => {
    const load = async () => {
      try {
        const response = await authService.getHomepageLayout();
        if (response.success && response.categories) {
          setCategories(response.categories);
        } else {
          setError(response.message || 'Impossible de charger la page d\'accueil');
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Impossible de charger la page d\'accueil');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const assignedElsewhere = (moduleId: string, categoryId: string) =>
    categories.some((category) => category.id !== categoryId && category.moduleIds.includes(moduleId));

  const updateCategory = (categoryId: string, patch: Partial<HomepageCategory>) => {
    setCategories((current) =>
      current.map((category) => (category.id === categoryId ? { ...category, ...patch } : category))
    );
    setSuccess(null);
  };

  const toggleModule = (categoryId: string, moduleId: string) => {
    setCategories((current) =>
      current.map((category) => {
        if (category.id === categoryId) {
          const hasModule = category.moduleIds.includes(moduleId);
          return {
            ...category,
            moduleIds: hasModule
              ? category.moduleIds.filter((id) => id !== moduleId)
              : [...category.moduleIds, moduleId],
          };
        }
        return {
          ...category,
          moduleIds: category.moduleIds.filter((id) => id !== moduleId),
        };
      })
    );
    setSuccess(null);
  };

  const moveCategory = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= categories.length) return;
    const next = [...categories];
    const [item] = next.splice(index, 1);
    next.splice(nextIndex, 0, item);
    setCategories(next);
    setSuccess(null);
  };

  const addCategory = () => {
    setCategories((current) => [
      ...current,
      { id: newCategoryId(), name: 'Nouvelle catégorie', moduleIds: [] },
    ]);
    setSuccess(null);
  };

  const removeCategory = (categoryId: string) => {
    setCategories((current) => current.filter((category) => category.id !== categoryId));
    setSuccess(null);
  };

  const handleSave = async () => {
    const cleaned = categories
      .map((category) => ({
        ...category,
        name: category.name.trim(),
        // Stocké déjà trié : la base reflète ainsi ce que le dashboard affiche.
        moduleIds: [...category.moduleIds].sort((a, b) => {
          const nameA = assignableModules.find((module) => module.id === a)?.name || a;
          const nameB = assignableModules.find((module) => module.id === b)?.name || b;
          return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
        }),
      }))
      .filter((category) => category.name);

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await authService.saveHomepageLayout(cleaned);
      if (response.success && response.categories) {
        setCategories(response.categories);
        onSaved?.(response.categories);
        setSuccess('Organisation enregistrée. Tous les utilisateurs voient cette page d\'accueil.');
      } else {
        setError(response.message || 'Enregistrement impossible');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const unassigned = assignableModules.filter(
    (module) => !categories.some((category) => category.moduleIds.includes(module.id))
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <div className="loading-spinner" />
        <p style={{ color: '#94a3b8' }}>Chargement de la page d'accueil…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '16px',
        marginBottom: '28px',
        flexWrap: 'wrap'
      }}>
        <div>
          <h2 style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 600, margin: '0 0 6px 0' }}>
            Page d'accueil
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0, maxWidth: '560px', lineHeight: 1.5 }}>
            Créez des catégories et assignez-y les applications. Cette organisation est identique pour
            tous les utilisateurs : chacun ne voit que les apps auxquelles il a accès.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={addCategory}
            style={{
              background: '#23262a',
              color: '#e2e8f0',
              border: '1px solid #353a40',
              borderRadius: '8px',
              padding: '10px 16px',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            + Catégorie
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: '#f59e0b',
              color: '#181b1f',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 18px',
              fontWeight: 600,
              cursor: saving ? 'wait' : 'pointer',
              fontSize: '0.875rem',
              opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          color: '#f87171',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          color: '#4ade80',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          {success}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {categories.map((category, index) => (
          <div
            key={category.id}
            style={{
              background: '#23262a',
              border: '1px solid #353a40',
              borderRadius: '12px',
              padding: '20px'
            }}
          >
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px' }}>
              <input
                value={category.name}
                onChange={(e) => updateCategory(category.id, { name: e.target.value })}
                placeholder="Nom de la catégorie"
                style={{
                  flex: 1,
                  background: '#181b1f',
                  border: '1px solid #353a40',
                  borderRadius: '8px',
                  color: '#fff',
                  padding: '10px 12px',
                  fontSize: '0.95rem',
                  fontWeight: 600
                }}
              />
              <button
                onClick={() => moveCategory(index, -1)}
                disabled={index === 0}
                title="Monter"
                style={iconButtonStyle(index === 0)}
              >
                ↑
              </button>
              <button
                onClick={() => moveCategory(index, 1)}
                disabled={index === categories.length - 1}
                title="Descendre"
                style={iconButtonStyle(index === categories.length - 1)}
              >
                ↓
              </button>
              <button
                onClick={() => removeCategory(category.id)}
                title="Supprimer"
                style={{ ...iconButtonStyle(false), color: '#f87171' }}
              >
                ✕
              </button>
            </div>

            <div className="mag-chips">
              {assignableModules.map((module) => {
                const checked = category.moduleIds.includes(module.id);
                const taken = assignedElsewhere(module.id, category.id);
                return (
                  <button
                    key={module.id}
                    type="button"
                    className={`mag-chip${checked ? ' is-on' : ''}`}
                    style={taken && !checked ? { opacity: 0.4 } : undefined}
                    title={taken && !checked ? 'Déjà dans une autre catégorie. Un clic la déplace ici.' : module.name}
                    onClick={() => toggleModule(category.id, module.id)}
                  >
                    {module.name}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {unassigned.length > 0 && (
        <div style={{ marginTop: '24px', color: '#64748b', fontSize: '0.85rem' }}>
          Non assignées (affichées dans « Autres » sur la page d'accueil) :{' '}
          {unassigned.map((module) => module.name).join(', ')}
        </div>
      )}
    </div>
  );
};

const iconButtonStyle = (disabled: boolean): React.CSSProperties => ({
  width: '36px',
  height: '36px',
  background: '#181b1f',
  border: '1px solid #353a40',
  borderRadius: '8px',
  color: '#cbd5e1',
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.35 : 1,
  fontSize: '0.95rem',
});

export default HomepageSettings;
